import { getRfqShopping } from "../api/ProcurementClient";
import Rfq from "../models/Rfq";
import { RfqHomework } from "../models/RfqHomework";
import { RfqItem } from "../models/RfqItem";
import { RfqItemShoppingCandidate } from "../models/RfqItemShoppingCandidate";
import { HomeworkShoppingTabState } from "../models/HomeworkShoppingTabState";

/**
 * Bumped on local RFQ scalar edits (status, etc.) so in-flight GETs / socket
 * broadcasts cannot overwrite newer client state (e.g. status snaps back to
 * OPEN on the work queue).
 */
const rfqClientEpochs = new Map<number, number>();

/** Ignore conflicting remote protected scalars until this timestamp (ms). */
const rfqLocalMutationUntil = new Map<number, number>();

/** How long after a local scalar edit we distrust mismatched socket/poll payloads. */
const LOCAL_MUTATION_GRACE_MS = 8_000;

const PROTECTED_SCALAR_KEYS = [
  "status",
  "priority",
  "assignedToId",
  "note",
  "quotedBy",
  "approvedBy",
  "approvedDate",
  "referenceNum",
  "dueDate",
  "poDueDate",
  "poNumber",
] as const;

type ProtectedScalarKey = (typeof PROTECTED_SCALAR_KEYS)[number];

function pickProtectedScalars(from: Rfq): Pick<Rfq, ProtectedScalarKey> {
  return {
    status: from.status,
    priority: from.priority,
    assignedToId: from.assignedToId,
    note: from.note,
    quotedBy: from.quotedBy,
    approvedBy: from.approvedBy,
    approvedDate: from.approvedDate,
    referenceNum: from.referenceNum,
    dueDate: from.dueDate,
    poDueDate: from.poDueDate,
    poNumber: from.poNumber,
  };
}

/**
 * Status is server-computed after order-confirmation changes, so it is only
 * protected while a local status edit is in flight.
 */
const rfqLocalStatusMutationUntil = new Map<number, number>();

/** True while `rfqId`'s deadline in `untilById` is in the future; drops expired ones. */
function isWithinGrace(untilById: Map<number, number>, rfqId: number): boolean {
  if (Date.now() < (untilById.get(rfqId) ?? 0)) return true;
  untilById.delete(rfqId);
  return false;
}

function keptLocalScalars(existing: Rfq, remote: Rfq): Partial<Rfq> {
  const kept: Partial<Rfq> = pickProtectedScalars(existing);
  if (!isWithinGrace(rfqLocalStatusMutationUntil, existing.id)) {
    kept.status = remote.status;
  }
  return kept;
}

function protectedScalarsMatch(a: Rfq, b: Rfq): boolean {
  const statusProtected = isWithinGrace(rfqLocalStatusMutationUntil, a.id);
  return PROTECTED_SCALAR_KEYS.every(
    (key) => (key === "status" && !statusProtected) || a[key] === b[key]
  );
}

export function bumpRfqClientEpoch(
  rfqId: number,
  options?: { statusEdit?: boolean }
): void {
  if (!Number.isFinite(rfqId) || rfqId <= 0) return;
  rfqClientEpochs.set(rfqId, (rfqClientEpochs.get(rfqId) ?? 0) + 1);
  const until = Date.now() + LOCAL_MUTATION_GRACE_MS;
  rfqLocalMutationUntil.set(rfqId, until);
  if (options?.statusEdit) {
    rfqLocalStatusMutationUntil.set(rfqId, until);
  }
}

export function getRfqClientEpoch(rfqId: number): number {
  return rfqClientEpochs.get(rfqId) ?? 0;
}

export function snapshotRfqClientEpochs(): Map<number, number> {
  return new Map(rfqClientEpochs);
}

/** Merge a partial RFQ update into the list, preserving fields omitted by PUT/detail responses. */
export function mergeRfqIntoList(prev: Rfq[], rfq: Rfq): Rfq[] {
  const index = prev.findIndex((r) => r.id === rfq.id);
  if (index < 0) {
    return [...prev, rfq];
  }

  const existing = prev[index];
  return prev.map((r, i) =>
    i === index
      ? {
          ...existing,
          ...rfq,
          emailReceivedAt: rfq.emailReceivedAt ?? existing.emailReceivedAt,
          purchaseOrders: rfq.purchaseOrders ?? existing.purchaseOrders,
          // Light status PUTs / detail without includeHistory omit history; keep loaded entries.
          assignmentHistory:
            rfq.assignmentHistory?.length
              ? rfq.assignmentHistory
              : existing.assignmentHistory,
          // Detail GET omits shopping payloads; keep any already-hydrated shopping data.
          quotes: mergeQuotesPreservingShopping(existing.quotes, rfq.quotes),
        }
      : r
  );
}

/**
 * Apply a GET /rfq/:id result. If a local mutation landed after the fetch started,
 * keep the newer local scalars (status, assignment, note, …).
 */
export function mergeFetchedRfqIntoList(
  prev: Rfq[],
  rfq: Rfq,
  epochAtFetchStart: number
): Rfq[] {
  if (getRfqClientEpoch(rfq.id) === epochAtFetchStart) {
    return mergeRfqIntoList(prev, rfq);
  }
  const existing = prev.find((r) => r.id === rfq.id);
  if (!existing) {
    return mergeRfqIntoList(prev, rfq);
  }
  // A newer local mutation landed after this GET started (e.g. vendor quote
  // save). Keep protected scalars AND the homework graph so stale payloads
  // cannot resurrect deleted part #s or wipe just-saved quote edits.
  return mergeRfqIntoList(prev, {
    ...rfq,
    ...keptLocalScalars(existing, rfq),
    quotes: existing.quotes,
  });
}

/**
 * Apply a socket (or other unsolicited) remote RFQ. Within the local-mutation
 * grace window, mismatched protected scalars are treated as stale and kept local.
 * Matching remotes end the grace early so peers can update again.
 */
export function mergeRemoteRfqIntoList(prev: Rfq[], rfq: Rfq): Rfq[] {
  const existing = prev.find((r) => r.id === rfq.id);
  if (!existing) {
    return mergeRfqIntoList(prev, rfq);
  }

  if (!isWithinGrace(rfqLocalMutationUntil, rfq.id)) {
    return mergeRfqIntoList(prev, rfq);
  }

  if (protectedScalarsMatch(existing, rfq)) {
    // Don't drop the grace window and take this broadcast's homework graph.
    // The broadcast can still be the pre-save quote (scratchpad price, then
    // the original number comes back) even when status and the other scalars match.
    return mergeRfqIntoList(prev, {
      ...rfq,
      quotes: existing.quotes,
    });
  }

  return mergeRfqIntoList(prev, {
    ...rfq,
    ...keptLocalScalars(existing, rfq),
    // Same grace window as scalars: don't let socket/poll clobber in-flight
    // vendor-quote / line-item edits with an older homework graph.
    quotes: existing.quotes,
  });
}

function mergeQuotesPreservingShopping(
  existing: RfqHomework[] | undefined,
  incoming: RfqHomework[] | undefined
): RfqHomework[] {
  if (!incoming) return existing ?? [];
  if (!existing?.length) return incoming;
  // Light PUT responses omit homework; keep already-loaded quotes.
  if (incoming.length === 0 && existing.length > 0) return existing;

  const existingById = new Map(existing.map((q) => [q.id, q]));
  return incoming.map((quote) => {
    const prev = existingById.get(quote.id);
    if (!prev) return quote;
    return {
      ...quote,
      // Session-slim payloads omit vendor RFQ send metadata; keep richer detail rows.
      vendorRfqs:
        isSessionSlimVendorRfqs(quote.vendorRfqs) &&
        (prev.vendorRfqs?.length ?? 0) > 0
          ? mergeVendorRfqsPreservingDetail(prev.vendorRfqs, quote.vendorRfqs)
          : (quote.vendorRfqs ?? prev.vendorRfqs ?? []),
      shoppingTabState: quote.shoppingTabState ?? prev.shoppingTabState ?? null,
      items: mergeItemsPreservingShopping(prev.items, quote.items),
    };
  });
}

/** Session list selects only id/vendorId — empty message id marks slim rows. */
function isSessionSlimVendorRfqs(
  vendorRfqs: RfqHomework["vendorRfqs"] | undefined
): boolean {
  if (!vendorRfqs?.length) return true;
  return vendorRfqs.every(
    (vr) => !vr.vendorRfqEmailMessageId || vr.vendorRfqEmailMessageId === ""
  );
}

function mergeVendorRfqsPreservingDetail(
  existing: NonNullable<RfqHomework["vendorRfqs"]>,
  incoming: NonNullable<RfqHomework["vendorRfqs"]>
): NonNullable<RfqHomework["vendorRfqs"]> {
  const incomingById = new Map(incoming.map((vr) => [vr.id, vr]));
  const merged = existing.map((prev) => {
    const next = incomingById.get(prev.id);
    if (!next) return prev;
    return { ...prev, vendorId: next.vendorId };
  });
  for (const next of incoming) {
    if (!existing.some((e) => e.id === next.id)) {
      merged.push(next);
    }
  }
  return merged;
}

/**
 * Session/list items omit name/description and ship OC-only vendor quotes.
 * When merging onto a detail-hydrated RFQ, keep the rich graph and patch OC fields.
 */
function isSessionSlimItem(item: RfqItem): boolean {
  const hasName = Boolean(item.name?.trim());
  const hasDescription = Boolean(item.description?.trim());
  if (hasName || hasDescription) return false;
  const quotes = item.vendorQuotes ?? [];
  if (quotes.length === 0) return true;
  return quotes.every(
    (q) =>
      (q.fees?.length ?? 0) === 0 &&
      (q.notes?.length ?? 0) === 0 &&
      (Array.isArray(q.url) ? q.url.length === 0 : !q.url) &&
      !(q.vendorName ?? "").trim()
  );
}

function mergeVendorQuotesPreservingDetail(
  existing: RfqItem["vendorQuotes"] | undefined,
  incoming: RfqItem["vendorQuotes"] | undefined
): RfqItem["vendorQuotes"] {
  if (!incoming?.length) return existing ?? [];
  if (!existing?.length) return incoming;

  const incomingById = new Map(incoming.map((q) => [q.id, q]));
  const merged = existing.map((prev) => {
    const next = incomingById.get(prev.id);
    if (!next) return prev;
    return {
      ...prev,
      orderConfirmation: next.orderConfirmation,
      orderConfirmationReceived: next.orderConfirmationReceived,
      orderEmailed: next.orderEmailed,
    };
  });
  for (const next of incoming) {
    if (!existing.some((e) => e.id === next.id)) {
      merged.push(next);
    }
  }
  return merged;
}

function mergeItemsPreservingShopping(
  existing: RfqItem[] | undefined,
  incoming: RfqItem[] | undefined
): RfqItem[] {
  if (!incoming) return existing ?? [];
  if (!existing?.length) return incoming;
  // Session/work-queue socket payloads use stub homework with items: [].
  if (incoming.length === 0 && existing.length > 0) return existing;

  const existingById = new Map(existing.map((item) => [item.id, item]));
  return incoming.map((item) => {
    const prev = existingById.get(item.id);
    if (!prev) return item;

    if (isSessionSlimItem(item) && !isSessionSlimItem(prev)) {
      const incomingCandidates = item.shoppingCandidates ?? [];
      const prevCandidates = prev.shoppingCandidates ?? [];
      const shoppingCandidates =
        incomingCandidates.length > 0 ? incomingCandidates : prevCandidates;
      return {
        ...prev,
        selectedVendorQuoteId: item.selectedVendorQuoteId,
        displayOrder: item.displayOrder ?? prev.displayOrder,
        vendorQuotes: mergeVendorQuotesPreservingDetail(
          prev.vendorQuotes,
          item.vendorQuotes
        ),
        shoppingCandidates,
      };
    }

    const incomingCandidates = item.shoppingCandidates ?? [];
    const prevCandidates = prev.shoppingCandidates ?? [];
    // Detail hydrate returns []; keep prior candidates until shopping endpoint applies.
    const shoppingCandidates =
      incomingCandidates.length > 0 ? incomingCandidates : prevCandidates;
    return { ...item, shoppingCandidates };
  });
}

export type RfqShoppingPayload = {
  quotes: Array<{
    id: number;
    shoppingTabState: HomeworkShoppingTabState | null;
    items: Array<{
      id: number;
      shoppingCandidates: RfqItemShoppingCandidate[];
    }>;
  }>;
};

/** Apply lazy-loaded shopping tab state + candidates onto an RFQ in the list. */
export function mergeRfqShoppingIntoList(
  prev: Rfq[],
  rfqId: number,
  shopping: RfqShoppingPayload
): Rfq[] {
  return prev.map((rfq) => {
    if (rfq.id !== rfqId) return rfq;

    const shoppingByHomeworkId = new Map(
      shopping.quotes.map((q) => [q.id, q])
    );

    return {
      ...rfq,
      quotes: rfq.quotes.map((homework) => {
        const shop = shoppingByHomeworkId.get(homework.id);
        if (!shop) return homework;

        const candidatesByItemId = new Map(
          shop.items.map((item) => [item.id, item.shoppingCandidates ?? []])
        );

        return {
          ...homework,
          shoppingTabState: shop.shoppingTabState ?? null,
          items: homework.items.map((item) => ({
            ...item,
            shoppingCandidates:
              candidatesByItemId.get(item.id) ?? item.shoppingCandidates ?? [],
          })),
        };
      }),
    };
  });
}

const shoppingLoadedRfqIds = new Set<number>();
const shoppingInflight = new Map<number, Promise<RfqShoppingPayload>>();

/** Drop cached shopping hydrate flag (e.g. after leaving an RFQ). */
export function clearRfqShoppingLoaded(rfqId: number): void {
  shoppingLoadedRfqIds.delete(rfqId);
  shoppingInflight.delete(rfqId);
}

type SetRfqs = (updater: (prev: Rfq[]) => Rfq[]) => void;

/** Lazy-load shopping tab state + candidates once per RFQ and merge into the list. */
export async function ensureRfqShoppingLoaded(
  rfqId: number,
  setRfqs: SetRfqs
): Promise<void> {
  if (!Number.isFinite(rfqId) || rfqId <= 0) return;
  if (shoppingLoadedRfqIds.has(rfqId)) return;

  let pending = shoppingInflight.get(rfqId);
  if (!pending) {
    pending = getRfqShopping(rfqId);
    shoppingInflight.set(rfqId, pending);
  }

  try {
    const shopping = await pending;
    shoppingLoadedRfqIds.add(rfqId);
    setRfqs((prev) => mergeRfqShoppingIntoList(prev, rfqId, shopping));
  } finally {
    shoppingInflight.delete(rfqId);
  }
}
